import { useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Inbox, Plus, CalendarDays, Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMobileNavBadges } from "@/hooks/useMobileNavBadges";

interface MobileBottomNavProps {
  onQuickCapture: () => void;
}

const navItems = [
  { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/inbox", icon: Inbox, label: "Inbox", badgeKey: "inbox" as const },
  { path: "capture", icon: Plus, label: "Capture", isCapture: true },
  { path: "/calendar", icon: CalendarDays, label: "Calendar", badgeKey: "calendar" as const },
  { path: "/settings", icon: Settings, label: "Settings" },
];

const BadgeIndicator = ({ count }: { count: number }) => {
  if (count === 0) return null;
  
  return (
    <span className="absolute -top-1 -right-1 h-4 min-w-4 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1">
      {count > 9 ? "9+" : count}
    </span>
  );
};

export const MobileBottomNav = ({ onQuickCapture }: MobileBottomNavProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { inboxCount, calendarTodayCount } = useMobileNavBadges();

  const handleNavClick = (item: typeof navItems[0]) => {
    if (item.isCapture) {
      onQuickCapture();
    } else {
      navigate(item.path);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  const getBadgeCount = (badgeKey?: "inbox" | "calendar") => {
    if (badgeKey === "inbox") return inboxCount;
    if (badgeKey === "calendar") return calendarTodayCount;
    return 0;
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border safe-area-bottom">
      <div className="flex items-center justify-around h-16 px-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = !item.isCapture && isActive(item.path);
          const badgeCount = getBadgeCount(item.badgeKey);

          if (item.isCapture) {
            return (
              <button
                key={item.path}
                onClick={() => handleNavClick(item)}
                className="flex flex-col items-center justify-center -mt-6"
                aria-label="Quick Capture"
              >
                <div className="flex items-center justify-center w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg">
                  <Icon className="h-6 w-6" />
                </div>
              </button>
            );
          }

          return (
            <button
              key={item.path}
              onClick={() => handleNavClick(item)}
              className={cn(
                "flex flex-col items-center justify-center gap-1 flex-1 h-full min-w-[64px] transition-colors",
                active ? "text-primary" : "text-muted-foreground"
              )}
              aria-label={item.label}
            >
              <div className="relative">
                <Icon className="h-5 w-5" />
                <BadgeIndicator count={badgeCount} />
              </div>
              <span className="text-xs font-medium">{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};
